import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormCheckbox from '@components/UI/FormCheckbox';
import FormSelect from '@components/UI/FormSelect';
import type { TradingProfile } from '@einhaus/project314-types';

interface BrokerageOrderRulesCardProps {
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
}

const BrokerageOrderRulesCard = ({ formData, setFormData }: BrokerageOrderRulesCardProps) => {
    return (
        <Card className="mb-4" title="Brokerage Order Rules">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="maximumEntryPriceIncreasePercent">
                        Maximum Entry Price Increase (%)
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormNumberInput
                        id="maximumEntryPriceIncreasePercent"
                        max={10}
                        min={0.01}
                        step={0.1}
                        value={formData.maximumEntryPriceIncreasePercent ?? null}
                        onValueChange={(value) =>
                            setFormData((previous) => ({
                                ...previous,
                                maximumEntryPriceIncreasePercent: value
                            }))
                        }
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        When the broker price at order time is above the price the entry was approved at, a rise up to this percent is
                        re-checked against the portfolio&apos;s cash, allocation, exposure, and governance limits and placed if they still
                        pass. Leave blank to reject any rise.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="cancelUnfilledBuyOrderMinutes">
                        Cancel Unfilled Buy Orders After Minutes
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormNumberInput
                        integer
                        data-name="brokerageOrderRules.cancelUnfilledBuyOrderMinutes"
                        id="cancelUnfilledBuyOrderMinutes"
                        min={0}
                        value={formData.brokerageOrderRules?.cancelUnfilledBuyOrderMinutes}
                        onValueChange={(value) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    cancelUnfilledBuyOrderMinutes: value
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="cancelUnfilledSellOrderMinutes">
                        Cancel Unfilled Sell Orders After Minutes
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormNumberInput
                        integer
                        data-name="brokerageOrderRules.cancelUnfilledSellOrderMinutes"
                        id="cancelUnfilledSellOrderMinutes"
                        min={0}
                        value={formData.brokerageOrderRules?.cancelUnfilledSellOrderMinutes}
                        onValueChange={(value) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    cancelUnfilledSellOrderMinutes: value
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="flex items-center mb-3">
                <FormCheckbox
                    checked={formData.brokerageOrderRules?.cancelUnfilledBuyOrdersAtClose || false}
                    id="cancelUnfilledBuyOrdersAtClose"
                    label="Cancel Unfilled Buy Orders At Close"
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            brokerageOrderRules: {
                                ...prev.brokerageOrderRules,
                                cancelUnfilledBuyOrdersAtClose: e.target.checked
                            }
                        }));
                    }}
                />
            </div>

            <div className="flex items-center mb-4">
                <FormCheckbox
                    checked={formData.brokerageOrderRules?.cancelUnfilledSellOrdersAtClose || false}
                    id="cancelUnfilledSellOrdersAtClose"
                    label="Cancel Unfilled Sell Orders At Close"
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            brokerageOrderRules: {
                                ...prev.brokerageOrderRules,
                                cancelUnfilledSellOrdersAtClose: e.target.checked
                            }
                        }));
                    }}
                />
            </div>

            {/* Buy Limit Order Settings */}
            <h5 className="mt-4 mb-3">Buy Limit Order Settings</h5>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-2">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="buyLimitTargetPercentage">
                        Buy Limit Target Percentage
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormNumberInput
                        data-name="brokerageOrderRules.buyLimitTargetPercentage"
                        id="buyLimitTargetPercentage"
                        step={0.1}
                        value={formData.brokerageOrderRules?.buyLimitTargetPercentage}
                        onValueChange={(value) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    buyLimitTargetPercentage: value
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-2">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="buyLimitBidAskAboveBelow">
                        Buy Limit Above/Below
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormSelect
                        id="buyLimitBidAskAboveBelow"
                        options={[
                            { value: '', label: 'Select Option' },
                            { value: 'above', label: 'Above' },
                            { value: 'below', label: 'Below' }
                        ]}
                        value={formData.brokerageOrderRules?.buyLimitBidAskAboveBelow ?? ''}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    buyLimitBidAskAboveBelow: (e.target.value as 'above' | 'below' | '') || null
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="buyLimitBidAskTarget">
                        Buy Limit Target
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormSelect
                        id="buyLimitBidAskTarget"
                        options={[
                            { value: '', label: 'Select Target' },
                            { value: 'ask', label: 'Ask' },
                            { value: 'bid', label: 'Bid' },
                            { value: 'askBidMid', label: 'Ask-Bid Midpoint' }
                        ]}
                        value={formData.brokerageOrderRules?.buyLimitBidAskTarget ?? ''}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    buyLimitBidAskTarget: (e.target.value as 'bid' | 'ask' | 'askBidMid' | '') || null
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            {/* Sell Limit Order Settings */}
            <h5 className="mt-4 mb-3">Sell Limit Order Settings</h5>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-2">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="sellLimitTargetPercentage">
                        Sell Limit Target Percentage
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormNumberInput
                        data-name="brokerageOrderRules.sellLimitTargetPercentage"
                        id="sellLimitTargetPercentage"
                        step={0.1}
                        value={formData.brokerageOrderRules?.sellLimitTargetPercentage}
                        onValueChange={(value) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    sellLimitTargetPercentage: value
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-2">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="sellLimitBidAskAboveBelow">
                        Sell Limit Above/Below
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormSelect
                        id="sellLimitBidAskAboveBelow"
                        options={[
                            { value: '', label: 'Select Option' },
                            { value: 'above', label: 'Above' },
                            { value: 'below', label: 'Below' }
                        ]}
                        value={formData.brokerageOrderRules?.sellLimitBidAskAboveBelow ?? ''}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    sellLimitBidAskAboveBelow: (e.target.value as 'above' | 'below' | '') || null
                                }
                            }));
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-3">
                <div className="md:col-span-6 flex items-center">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="sellLimitBidAskTarget">
                        Sell Limit Target
                    </label>
                </div>
                <div className="md:col-span-6">
                    <FormSelect
                        id="sellLimitBidAskTarget"
                        options={[
                            { value: '', label: 'Select Target' },
                            { value: 'ask', label: 'Ask' },
                            { value: 'bid', label: 'Bid' },
                            { value: 'askBidMid', label: 'Ask-Bid Midpoint' }
                        ]}
                        value={formData.brokerageOrderRules?.sellLimitBidAskTarget ?? ''}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                brokerageOrderRules: {
                                    ...prev.brokerageOrderRules,
                                    sellLimitBidAskTarget: (e.target.value as 'bid' | 'ask' | 'askBidMid' | '') || null
                                }
                            }));
                        }}
                    />
                </div>
            </div>
        </Card>
    );
};

export default BrokerageOrderRulesCard;
